import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import {
  ICancelExecutionMutation,
  ICancelExecutionMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { CANCEL_EXECUTION } from '../../../../utils/apollo/analysis';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../../utils/types';
import toastMessage from '../../../../utils/toastMessage';
import { TextButton } from '../../../Button';

interface IProps {
  analysisId: string;
}

const StyledTextButton = styled(TextButton)`
  align-self: center;
  margin-top: 30px;
  font-weight: 500;
`;

const CancelQuery = ({ analysisId }: IProps) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>();

  const handleMutationComplete = ({
    analysisCancel,
  }: ICancelExecutionMutation) => {
    const { errors, message } = analysisCancel;

    // Just in case, enable button in 2 seconds
    timeoutIdRef.current = setTimeout(() => {
      setIsDisabled(false);
    }, 2000);

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(message);
    toastMessage({
      type: 'info',
      message,
    });
  };

  const [cancelExecution] = useMutation<
    ICancelExecutionMutation,
    ICancelExecutionMutationVariables
  >(CANCEL_EXECUTION, {
    onCompleted: handleMutationComplete,
  });

  const handleClick = () => {
    setIsDisabled(true);
    cancelExecution({
      variables: {
        input: {
          analysisId,
        },
      },
    });
  };

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  return (
    <StyledTextButton onClick={handleClick} disabled={isDisabled}>
      Cancel Query
    </StyledTextButton>
  );
};

export default CancelQuery;
